import type {
  CompleteCheckoutRequestDto,
  ExecuteCheckinRequestDto,
  MoveStayRoomRequestDto,
  RegisterGuestDocumentRequestDto,
} from "./contracts/dtos.ts";
import {
  validateCompleteCheckoutRequest,
  validateExecuteCheckinRequest,
  validateMoveStayRoomRequest,
  validateRegisterGuestDocumentRequest,
} from "./contracts/schemas.ts";
import { isRoomReadyForCheckin } from "../housekeeping/service.ts";
import type { FrontDeskInvoiceProvider } from "./invoice.ts";
import type { FrontDeskKeycardProvider } from "./keycard.ts";
import { canExecuteCheckin, canExecuteCheckout, canMoveRoom, canRegisterDocument } from "./permissions.ts";
import type {
  FrontDeskAuditEntry,
  FrontDeskContext,
  FrontDeskInvoice,
  FrontDeskKeycardRecord,
  FrontDeskPaymentSettlement,
  FrontDeskRoomMove,
  FrontDeskContextStay,
  GuestDocumentRecord,
} from "./types.ts";

type IdFactory = (prefix: string) => string;

function buildAudit(
  idFactory: IdFactory,
  now: string,
  input: Omit<FrontDeskAuditEntry, "id" | "timestamp">
): FrontDeskAuditEntry {
  return {
    ...input,
    id: idFactory("fd-audit"),
    timestamp: now,
  };
}

export function registerGuestDocument(
  request: RegisterGuestDocumentRequestDto,
  now: string,
  dependencies: { idFactory: IdFactory }
) {
  const fieldErrors = validateRegisterGuestDocumentRequest(request);
  if (Object.keys(fieldErrors).length > 0) {
    return { ok: false as const, message: "Document registration is invalid.", fieldErrors };
  }

  if (!canRegisterDocument(request.actor.role)) {
    return { ok: false as const, message: "You do not have permission to register documents." };
  }

  const document: GuestDocumentRecord = {
    id: request.documentId,
    guestId: request.guestId,
    reservationId: request.reservationId,
    type: request.type,
    documentNumber: request.documentNumber.trim(),
    nationality: request.nationality.trim(),
    expiresAt: request.expiresAt,
    issuedBy: request.issuedBy?.trim() || undefined,
    status: "verified",
    registeredAt: now,
    verifiedAt: now,
    note: request.note?.trim() || undefined,
    actor: request.actor.displayName,
  };

  return {
    ok: true as const,
    document,
    auditEntry: buildAudit(dependencies.idFactory, now, {
      action: "document_registered",
      reservationId: request.reservationId,
      guestId: request.guestId,
      actor: request.actor.displayName,
      message: `Document ${request.type} registered for guest ${request.guestId}.`,
    }),
  };
}

export async function executeCheckin(
  request: ExecuteCheckinRequestDto,
  context: FrontDeskContext,
  dependencies: { idFactory: IdFactory; keycardProvider: FrontDeskKeycardProvider }
) {
  const fieldErrors = validateExecuteCheckinRequest(request);
  if (Object.keys(fieldErrors).length > 0) {
    return { ok: false as const, message: "Check-in request is invalid.", fieldErrors };
  }
  if (!canExecuteCheckin(request.actor.role)) {
    return { ok: false as const, message: "You do not have permission to complete check-in." };
  }

  const reservation = context.reservations.find((entry) => entry.id === request.reservationId);
  if (!reservation) return { ok: false as const, message: "Reservation not found." };
  if (reservation.status !== "booked" && reservation.status !== "checkin" && reservation.status !== "pending_confirmation") {
    return { ok: false as const, message: "Reservation is not eligible for check-in." };
  }

  const room = context.rooms.find((entry) => entry.id === request.roomId);
  if (!room) return { ok: false as const, message: "Room not found." };
  if (room.typeId !== reservation.roomTypeId) {
    return { ok: false as const, message: "Selected room does not match the reserved room type." };
  }
  if (!isRoomReadyForCheckin(room)) {
    return { ok: false as const, message: "No ready room available for check-in." };
  }

  const guest = context.guests.find((entry) => entry.id === reservation.guestId);
  if (!guest) return { ok: false as const, message: "Guest not found." };
  if (!request.documentChecked) return { ok: false as const, message: "Confirm that the guest document has been checked." };

  const stay: FrontDeskContextStay = {
    id: dependencies.idFactory("stay"),
    reservationId: reservation.id,
    guestId: reservation.guestId,
    roomId: room.id,
    arrival: reservation.arrival,
    departure: reservation.departure,
    status: "inhouse",
  };

  let keycard: FrontDeskKeycardRecord | undefined;
  if (request.issueKeycard) {
    const issued = await dependencies.keycardProvider.issueKeycard({
      stayId: stay.id,
      reservationId: reservation.id,
      roomId: room.id,
      guestId: reservation.guestId,
    });
    keycard = {
      id: dependencies.idFactory("fd-key"),
      stayId: stay.id,
      reservationId: reservation.id,
      roomId: room.id,
      guestId: reservation.guestId,
      status: "active",
      encodedAt: context.now,
      encoderReference: issued.encoderReference,
      provider: issued.provider,
    };
  }

  const auditEntries: FrontDeskAuditEntry[] = [
    buildAudit(dependencies.idFactory, context.now, {
      action: "checkin_completed",
      reservationId: reservation.id,
      stayId: stay.id,
      guestId: reservation.guestId,
      actor: request.actor.displayName,
      message: `Check-in completed for reservation ${reservation.id}.`,
      metadata: {
        roomId: room.id,
      },
    }),
  ];

  if (keycard) {
    auditEntries.push(
      buildAudit(dependencies.idFactory, context.now, {
        action: "keycard_issued",
        reservationId: reservation.id,
        stayId: stay.id,
        guestId: reservation.guestId,
        actor: request.actor.displayName,
        message: `Keycard issued for room ${room.number}.`,
      })
    );
  }

  return {
    ok: true as const,
    stay,
    keycard,
    roomId: room.id,
    auditEntries,
  };
}

export async function moveStayRoom(
  request: MoveStayRoomRequestDto,
  context: FrontDeskContext,
  dependencies: { idFactory: IdFactory; keycardProvider: FrontDeskKeycardProvider }
) {
  const fieldErrors = validateMoveStayRoomRequest(request);
  if (Object.keys(fieldErrors).length > 0) {
    return { ok: false as const, message: "Room move request is invalid.", fieldErrors };
  }
  if (!canMoveRoom(request.actor.role)) {
    return { ok: false as const, message: "You do not have permission to move rooms." };
  }

  const stay = context.stays.find((entry) => entry.id === request.stayId && entry.status === "inhouse");
  if (!stay) return { ok: false as const, message: "Active stay not found." };
  const reservation = context.reservations.find((entry) => entry.id === stay.reservationId);
  if (!reservation) return { ok: false as const, message: "Reservation not found." };

  const toRoom = context.rooms.find((entry) => entry.id === request.toRoomId);
  if (!toRoom) return { ok: false as const, message: "Target room not found." };
  if (!isRoomReadyForCheckin(toRoom)) return { ok: false as const, message: "Target room is not ready." };
  if (toRoom.typeId !== reservation.roomTypeId) return { ok: false as const, message: "Target room type does not match reservation." };

  const roomMove: FrontDeskRoomMove = {
    id: dependencies.idFactory("room-move"),
    stayId: stay.id,
    reservationId: reservation.id,
    guestId: stay.guestId,
    fromRoomId: stay.roomId,
    toRoomId: toRoom.id,
    movedAt: context.now,
    actor: request.actor.displayName,
    reason: request.reason?.trim() || undefined,
    replacementKeyIssued: request.issueReplacementKey,
  };

  let replacementKeycard: FrontDeskKeycardRecord | undefined;
  if (request.issueReplacementKey) {
    const issued = await dependencies.keycardProvider.issueKeycard({
      stayId: stay.id,
      reservationId: reservation.id,
      roomId: toRoom.id,
      guestId: stay.guestId,
    });
    replacementKeycard = {
      id: dependencies.idFactory("fd-key"),
      stayId: stay.id,
      reservationId: reservation.id,
      roomId: toRoom.id,
      guestId: stay.guestId,
      status: "active",
      encodedAt: context.now,
      encoderReference: issued.encoderReference,
      provider: issued.provider,
    };
  }

  return {
    ok: true as const,
    roomMove,
    replacementKeycard,
    auditEntries: [
      buildAudit(dependencies.idFactory, context.now, {
        action: "room_moved",
        reservationId: reservation.id,
        stayId: stay.id,
        guestId: stay.guestId,
        actor: request.actor.displayName,
        message: `Stay ${stay.id} moved to room ${toRoom.number}.`,
      }),
    ],
  };
}

export async function completeCheckout(
  request: CompleteCheckoutRequestDto,
  context: FrontDeskContext,
  dependencies: { idFactory: IdFactory; invoiceProvider: FrontDeskInvoiceProvider }
) {
  const fieldErrors = validateCompleteCheckoutRequest(request);
  if (Object.keys(fieldErrors).length > 0) {
    return { ok: false as const, message: "Checkout request is invalid.", fieldErrors };
  }
  if (!canExecuteCheckout(request.actor.role)) {
    return { ok: false as const, message: "You do not have permission to complete checkout." };
  }

  const stay = context.stays.find((entry) => entry.id === request.stayId && entry.status === "inhouse");
  if (!stay) return { ok: false as const, message: "Active stay not found." };
  const reservation = context.reservations.find((entry) => entry.id === stay.reservationId);
  if (!reservation) return { ok: false as const, message: "Reservation not found." };
  const folio = context.folios.find(
    (entry) => entry.stayId === stay.id && (entry.status === "open" || entry.status === "settlement_pending")
  );
  if (!folio) return { ok: false as const, message: "Open folio not found." };
  if (request.settlementAmount < folio.balance) {
    return { ok: false as const, message: "Checkout is blocked because the folio remains open." };
  }

  let settlement: FrontDeskPaymentSettlement | undefined;
  if (folio.balance > 0) {
    settlement = {
      id: dependencies.idFactory("settlement"),
      folioId: folio.folioId,
      reservationId: reservation.id,
      stayId: stay.id,
      amount: folio.balance,
      currency: request.currency,
      method: request.settlementMethod,
      status: "settled",
      createdAt: context.now,
      actor: request.actor.displayName,
    };
  }

  try {
    const invoiceResult = await dependencies.invoiceProvider.generateInvoice({
      folioId: folio.folioId,
      reservationId: reservation.id,
      stayId: stay.id,
      guestId: stay.guestId,
      totalAmount: folio.balance,
      currency: request.currency,
      emailInvoice: request.emailInvoice,
      printInvoice: request.printInvoice,
    });
    const invoice: FrontDeskInvoice = {
      id: dependencies.idFactory("invoice"),
      folioId: folio.folioId,
      reservationId: reservation.id,
      stayId: stay.id,
      guestId: stay.guestId,
      totalAmount: folio.balance,
      currency: request.currency,
      status: request.emailInvoice ? "emailed" : request.printInvoice ? "printed" : "generated",
      generatedAt: context.now,
      printedAt: request.printInvoice ? context.now : undefined,
      emailedAt: request.emailInvoice ? context.now : undefined,
      provider: invoiceResult.provider,
      providerDocumentId: invoiceResult.documentId,
    };
    return {
      ok: true as const,
      settlement,
      invoice,
      auditEntries: [
        buildAudit(dependencies.idFactory, context.now, {
          action: "checkout_settled",
          reservationId: reservation.id,
          stayId: stay.id,
          guestId: stay.guestId,
          actor: request.actor.displayName,
          message: `Checkout settled for stay ${stay.id}.`,
        }),
        buildAudit(dependencies.idFactory, context.now, {
          action: "invoice_generated",
          reservationId: reservation.id,
          stayId: stay.id,
          guestId: stay.guestId,
          actor: request.actor.displayName,
          message: `Invoice generated for stay ${stay.id}.`,
        }),
      ],
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : "Invoice generation failed.";
    return {
      ok: false as const,
      message,
      auditEntries: [
        buildAudit(dependencies.idFactory, context.now, {
          action: "invoice_failed",
          reservationId: reservation.id,
          stayId: stay.id,
          guestId: stay.guestId,
          actor: request.actor.displayName,
          message,
        }),
      ],
    };
  }
}
